import { authorized } from "../authorized";
import { fetchWithCredentials } from "../fetch";
import { AccountRegion } from "../types/regionalApi";
import {
  UnifiedProvisionApiErrorMessage,
  UnifiedProvisionError,
  UnifiedProvisionPlanError,
} from "./constants";
import {
  bannerStatus,
  checkStatusCodeForError,
  is2faForbiddenError,
  parseErrorMessageFromResponse,
} from "./error";

export interface APIError {
  field?: string;
  message?: string;
  error_id?: string;
  help?: string;
}

export interface APIResponse {
  errors?: APIError[];
  status: number;
}

export interface FetchUnifiedSearchResponse extends APIResponse {
  result: { id: number; type: "mp" | "unified" }[];
}

export type FetchIdentifierResult =
  | {
      success: true;
      login_type: string;
    }
  | {
      success: false;
      reason: string;
    };

interface FetchIdentifierResponse extends APIResponse {
  login_type: string;
}

export const fetchIdentifier = async (
  username: string,
  bot_detection_token?: string
): Promise<FetchIdentifierResult> => {
  const baseURL = getBaseURL();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(bot_detection_token && { "Bot-Detection-Token": bot_detection_token }),
  };
  // Routing for SSO, the IdP, and Basic Auth
  // Build the request for the gandalf identifier endpoint
  // Call the gandalf identifier endpoint
  let response: Response | undefined;
  try {
    response = await fetchWithCredentials(`${baseURL}/v3/public/identifier`, {
      timeout: 5000,
      method: "POST",
      headers,
      body: JSON.stringify({ username }),
    });
  } catch (error: any) {
    if (error.name !== "AbortError") {
      // If it's not an AbortError throw if it is an abort error it just means we timed out.
      throw error;
    }
  }
  if (!response) {
    return {
      success: false,
      reason: bannerStatus.GeneralIdentifierError.banner,
    };
  }
  let body: FetchIdentifierResponse;
  try {
    if (response.status === 404) {
      return {
        success: false,
        reason: "ERR_USERNAME_NOT_FOUND",
      };
    }
    body = await response.json();
    if (response.status === 400) {
      if (body?.errors?.[0]?.message) {
        return {
          success: false,
          reason: body?.errors?.[0]?.message,
        };
      }
      return {
        success: false,
        reason: bannerStatus.GeneralIdentifierError.banner,
      };
    }
    if (!response.ok) {
      return {
        success: false,
        reason: bannerStatus.GeneralIdentifierError.banner,
      };
    }
    return {
      success: true,
      login_type: body?.login_type,
    };
  } catch (error) {
    return {
      success: false,
      reason: bannerStatus.GeneralIdentifierError.banner,
    };
  }
};

export interface FetchSSOResponse extends APIResponse {
  idp_url: string;
  saml_request: string;
  relay_state: string;
}

export const fetchSSO = async (username: string): Promise<Response> => {
  const baseURL = getBaseURL();
  return fetchWithCredentials(
    `${baseURL}/v3/public/sso/saml/request/${encodeURIComponent(username)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    }
  );
};

export type FetchAuthenticateTokenResult =
  | {
      success: true;
      token: string;
      password_reset_required: boolean;
      setup_2fa_required: boolean;
      region: AccountRegion;
    }
  | {
      success: false;
      redirect: boolean;
      auth_type?: string;
      reason?: JSX.Element;
    };

interface FetchAuthenticateTokenResponse extends APIResponse {
  password_reset_required?: boolean;
  setup_2fa_required?: boolean;
  token?: string;
  region: AccountRegion;
  enrichment_info?: {
    type?: string;
  };
}

export const fetchAuthenticateToken = async (
  username: string,
  password: string,
  bot_detection_token?: string
): Promise<FetchAuthenticateTokenResult> => {
  const baseURL = getBaseURL();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(bot_detection_token && { "Bot-Detection-Token": bot_detection_token }),
  };
  const response = await fetchWithCredentials(`${baseURL}/v3/public/tokens`, {
    method: "POST",
    headers,
    body: JSON.stringify({ username, password }),
  });
  const body: FetchAuthenticateTokenResponse = await response.json();
  let loginType = "mp-basic";
  if (
    body &&
    body.errors &&
    body.errors.length > 0 &&
    body.errors[0].error_id === "ERROR_ID_SSO_OR_UNIFIED_USER_BASIC_LOGIN" &&
    body.errors[0].field === "login_type"
  ) {
    loginType = body.errors[0].message ?? "mp-basic";
  }
  if (checkStatusCodeForError(response.status)) {
    return {
      success: false,
      // if it's a sso or unified login
      redirect: response.status === 400,
      auth_type: loginType,
      reason: parseErrorMessageFromResponse({
        status: response.status,
        errors: body?.errors,
      }),
    };
  }
  const { token = "", password_reset_required = false, setup_2fa_required = false, region } = body;
  return {
    success: true,
    token,
    password_reset_required,
    setup_2fa_required,
    region,
  };
};

export const deleteAuthenticateToken = async (
  token: string,
  region?: AccountRegion
): Promise<Response> => {
  const baseURL = getBaseURL();

  const fetch = authorized({ token, region: region ?? AccountRegion.Global });
  return fetch(`${baseURL}/v3/tokens`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token: token }),
  });
};

export const fetchTeammate = async (
  token: string,
  username: string,
  region: AccountRegion
): Promise<Response> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  return fetch(`${baseURL}/v3/teammates/${username}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const fetchAccountProfile = async (
  token: string,
  region: AccountRegion
): Promise<Response> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  return fetch(`${baseURL}/v3/account/profile_v2`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const fetchFriendlyName = async (
  token: string,
  friendlyName: string,
  region: AccountRegion
): Promise<Response> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  const requestBody = {
    friendly_name: friendlyName,
  };

  return fetch(`${baseURL}/v3/account/profile_v2`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });
};

export const fetchUserType = async (token: string, region: AccountRegion): Promise<Response> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region: region });
  return fetch(`${baseURL}/v3/user/type`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

interface FetchTwoFAResponse extends APIResponse {
  is_verified?: boolean;
}

export type FetchTwoFAResult =
  | {
      success: true;
      is_verified: boolean;
    }
  | {
      success: false;
      reason?: JSX.Element;
    };

export const fetchTwoFASetting = async (
  token: string,
  region: AccountRegion
): Promise<FetchTwoFAResult> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  const response = await fetch(`${baseURL}/v3/public/access_settings/multifactor`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response) {
    return { success: false, reason: <>bannerStatus.TwoFAError.banner</> };
  }
  if (response.status === 404) {
    return {
      success: true,
      is_verified: false,
    };
  }
  let body: FetchTwoFAResponse | undefined;
  try {
    body = (await response.json()) as FetchTwoFAResponse;
  } catch (e) {}
  if (response.status !== 200 && response.status !== 404) {
    return {
      success: false,
      reason: parseErrorMessageFromResponse({
        status: response.status,
        errors: body?.errors,
      }),
    };
  }
  return {
    success: true,
    is_verified: body?.is_verified ?? false,
  };
};

/**
 * Session Token Results and Authenticate Token Results are identical types they duplicate
 * the fields since they are pulling different information.
 */
export type FetchSessionTokenResult =
  | {
      success: true;
      token: string;
      password_reset_required?: boolean;
      setup_2fa_required?: boolean;
      region: AccountRegion;
    }
  | {
      success: false;
      reason?: JSX.Element | string;
    };

interface FetchSessionTokenResponse extends APIResponse {
  password_reset_required?: boolean;
  setup_2fa_required?: boolean;
  token?: string;
  region: AccountRegion;
}

// Fetch the session token and navigate to session token landing point.
export const fetchSessionToken = async (
  access_token: string,
  userId?: string
): Promise<FetchSessionTokenResult> => {
  let response: Response | undefined;
  try {
    const baseURL = getBaseURL();
    response = await fetchWithCredentials(`${baseURL}/v3/sessions`, {
      timeout: 10000,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: userId
        ? JSON.stringify({
            user_id: Number(userId),
          })
        : undefined,
    });
  } catch (error: any) {
    if (error.name !== "AbortError") {
      // If it's not an AbortError throw if it is an abort error it just means we timed out.
      throw error;
    }
  }
  if (!response) {
    return { success: false, reason: bannerStatus.GeneralSessionTokenError.banner };
  }
  if (!response.ok && response.status === 401) {
    // Just means the user doesn't exist
    return { success: false };
  }
  if (!response.ok && response.status === 403) {
    return {
      success: false,
      reason: bannerStatus.UserForbiddenAccessError.banner,
    };
  }

  let body: FetchSessionTokenResponse;
  try {
    body = await response.json();
  } catch {
    return {
      success: false,
      reason: bannerStatus.GeneralSessionTokenError.banner,
    };
  }
  const { token = "", password_reset_required = false, setup_2fa_required = false, region } = body;
  if (response.status >= 200 && response.status < 300 && body) {
    // Means the user exists and is ready
    return {
      success: true,
      token,
      password_reset_required,
      setup_2fa_required,
      region,
    };
  }
  // If anything else shows up throw a fetchError based on the return status.
  if (checkStatusCodeForError(response.status)) {
    // Check if it's an IPAM error
    const errors = body?.errors ?? [];
    const ipamError = errors.some(
      (err) => err.message === "The requestor's IP Address is not whitelisted"
    );

    const multiAccountTeammateError = errors.some(
      (err) => err.error_id === "ERROR_ID_MULTI_ACCOUNT_ACCESS_MUST_SPECIFY_USER_ID"
    );
    if (multiAccountTeammateError) {
      return {
        success: false,
        reason: "user ID not specified for multi-account access teammate",
      };
    }

    if (ipamError) {
      return {
        success: false,
        reason: "The requestor's IP Address is not whitelisted",
      };
    }

    // If it's not the IPAM error return the error message.
    return {
      success: false,
      reason: parseErrorMessageFromResponse({
        status: response.status,
        errors,
      }),
    };
  }
  return {
    success: false,
    reason: bannerStatus.GeneralSessionTokenError.banner,
  };
};

export interface FetchAcceptInviteResponse extends APIResponse {
  token: string;
  setup_2fa_required: boolean;
}

export interface FetchAcceptInviteBody {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
}

export const fetchAcceptInvite = async (
  token: string,
  body: FetchAcceptInviteBody
): Promise<Response> => {
  const baseURL = getBaseURL();
  return fetchWithCredentials(`${baseURL}/v3/public/teammates/${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
};

export const fetchUnifiedSearch = async (access_token: string, id_token: string) => {
  const baseURL = getBaseURL();
  // unauthenticated endpoint in sg-gateway, using global region.
  const fetch = authorized({ bearer: access_token, region: AccountRegion.Global });
  return fetch(`${baseURL}/v3/unified_search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id_token,
    }),
  });
};

export const fetchUnifiedSignup = async (
  access_token: string,
  id_token: string
): Promise<Response> => {
  const baseURL = getBaseURL();
  // unauthenticated endpoint in sg-gateway, using global region.
  const fetch = authorized({ bearer: access_token, region: AccountRegion.Global });
  return fetch(`${baseURL}/v3/unified_signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id_token,
    }),
  });
};
export const fetchUnifiedLink = async (
  access_token: string,
  accept_token: string,
  id_token: string
): Promise<APIResponse | null> => {
  const baseURL = getBaseURL();
  // unauthenticated endpoint in sg-gateway, using global region.
  const fetch = authorized({ bearer: access_token, region: AccountRegion.Global });
  const response = await fetch(`${baseURL}/v3/unified_link_v2/${accept_token}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token }),
  });

  if (response.ok) return null;

  try {
    const body: APIResponse = await response.json();
    return {
      status: response.status,
      errors: body.errors ?? [
        {
          error_id: "ERR_TEAMMATE_INVITE_INTERNAL",
          message: "A problem occurred while accepting the invite.",
        },
      ],
    };
  } catch {
    return {
      status: response.status,
      errors: [
        {
          error_id: "ERR_TEAMMATE_INVITE_INTERNAL",
          message: "Received an unexpected response while accepting invite.",
        },
      ],
    };
  }
};

export type UnifiedProvisionPlanResponse =
  | {
      eligible: true;
      email: string;
      auth0_login_exists: boolean;
      rollout_phase: number;
      base: number;
      has_mfa_exemption?: boolean;
    }
  | {
      eligible: false;
      reason: string;
      errors?: APIError[];
      has_mfa_exemption?: boolean;
    };

/**
 * Call unified provision plan to check account linking eligibility.
 * @param token
 * @param region
 */
export const fetchUnifiedProvisionPlan = async (
  token: string,
  region: AccountRegion
): Promise<UnifiedProvisionPlanResponse> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  const response = await fetch(`${baseURL}/v3/unified_provision/plan`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const responseJson = await response.json();
    if (response.status === 403 && is2faForbiddenError(responseJson)) {
      return {
        eligible: false,
        reason: UnifiedProvisionPlanError.VALIDATE_2FA_REQUIRED,
      };
    }
    return {
      eligible: false,
      reason: UnifiedProvisionPlanError.GENERAL_ERROR,
    };
  }
  return await response.json();
};

export type UnifiedProvisionResponse =
  | {
      success: true;
    }
  | {
      success: false;
      reason: UnifiedProvisionError;
      errors?: APIError[];
    };

/**
 * Call unified provision to link sendgrid account to twilio account.
 * @param token
 * @param id_token
 */
export const fetchUnifiedProvision = async (
  token: string,
  id_token: string,
  region: AccountRegion
): Promise<UnifiedProvisionResponse> => {
  const baseURL = getBaseURL();
  const fetch = authorized({ token, region });
  const response = await fetch(`${baseURL}/v3/unified_provision`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id_token,
    }),
  });
  if (!response.ok) {
    const responseBody = await response.json();
    let error = UnifiedProvisionError.GENERAL_ERROR;
    if (
      response.status === 400 &&
      responseBody.errors[0].message === UnifiedProvisionApiErrorMessage.USERNAME_IN_USE
    ) {
      error = UnifiedProvisionError.USERNAME_IN_USE;
    }
    if (
      response.status === 400 &&
      responseBody.errors[0].message === UnifiedProvisionApiErrorMessage.EMAIL_MISMATCH
    ) {
      error = UnifiedProvisionError.IDP_EMAIL_MISMATCH;
    }
    return { success: false, reason: error };
  }
  return { success: true };
};

const getBaseURL = (): string => {
  return import.meta.env.VITE_API_BASE_URL || "https://api.sendgrid.com";
};

export interface UnifiedAccount {
  account_id: string;
  user_id: string;
  type: string;
  friendly_name: string;
  status_id: string;
  status_display: string;
}

export interface UnifiedAccountsResponse {
  results: UnifiedAccount[] | null;
  error?: any | null;
}

/**
 * Fetch the unified accounts for the current user.
 * @param access_token - The IdP access token for authorization.
 * @returns An object with either results or error set.
 */
export const getUnifiedAccounts = async (
  access_token: string
): Promise<UnifiedAccountsResponse> => {
  const baseURL = import.meta.env.VITE_API_BASE_URL || "https://api.sendgrid.com";
  const response = await fetchWithCredentials(`${baseURL}/v3/unified_users/accounts`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${access_token}`,
    },
  });

  if (!response.ok) {
    return { results: null, error: await response.json() };
  }
  const { results } = (await response.json()) as { results: UnifiedAccount[] };
  return { results, error: null };
};

// Exponential backoff with jitter, starting at 1s and maxing at 8s.
function getBackoffDelay(attempt: number): number {
  const base = 1000 * 2 ** attempt;
  const cap = Math.min(base, 8000);
  const jitter = Math.floor(Math.random() * 300);
  return cap + jitter;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Decide if a response status should be retried on this attempt.
function shouldRetry(status: number, attempt: number, lastAttemptIndex: number): boolean {
  if (status >= 500 && status <= 599) return attempt < lastAttemptIndex;

  // The S2S status endpoint returns 404 when S2S entry is not yet created
  if (status === 404) {
    return attempt < lastAttemptIndex;
  }

  return false;
}

// fetchStatus queries the S2S signup/status endpoint to check if the profile is complete.
// - If complete → return true (caller can redirect to onboarding).
// - If not complete → retry with exponential backoff + jitter.
// Retries give the backend time to finish creating the sessionToken.
export async function fetchStatus(token: string, accountRegion: AccountRegion): Promise<boolean> {
  const baseURL = getBaseURL();
  const apiFetch = authorized({ token, region: accountRegion });

  const MAX_ATTEMPTS = 5;
  const LAST_ATTEMPT_INDEX = MAX_ATTEMPTS - 1;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const res = await apiFetch(`${baseURL}/v3/s2s/signup/status`, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        if (shouldRetry(res.status, attempt, LAST_ATTEMPT_INDEX)) {
          await sleep(getBackoffDelay(attempt));
          continue;
        }

        return false;
      }

      let body: any;
      try {
        body = await res.json();
      } catch {
        return false;
      }

      if (body?.profile_complete === true) {
        return true;
      }

      if (attempt < LAST_ATTEMPT_INDEX) {
        await sleep(getBackoffDelay(attempt));
        continue;
      }

      return false;
    } catch {
      if (attempt < LAST_ATTEMPT_INDEX) {
        await sleep(getBackoffDelay(attempt));
        continue;
      }

      return false;
    }
  }

  return false;
}

export type FetchVerifyEmailResult =
  | {
      verified: true;
      target: string;
    }
  | {
      verified: false;
      error_id: string;
    };

interface FetchVerifyEmailResponse {
  target?: string;
  verified?: boolean;
  error_id?: string;
  errors?: APIError[];
}

const defaultVerifyEmailError: FetchVerifyEmailResult = {
  verified: false,
  error_id: "ERR_TEAMMATE_INVITE_INTERNAL",
};

/**
 * Verify an invited teammate's email against their invite token.
 *
 * Not idempotent: the backend calls ValidateToken, which decrements a bounded validation counter on
 * the token (max 20 for this token type; also validated once on the acceptance side via
 * /v3/unified_link_v2). The identifier page re-enables its submit button after an error/timeout, so a
 * user can retry — this is acceptable because the side effect is only the counter, retries require a
 * deliberate click, and once the counter is exhausted the endpoint returns
 * ERR_TEAMMATE_INVITE_INVALID_TOKEN, which the page maps to the "invalid or expired — start over"
 * message (i.e. it degrades to the correct recovery path, not a dead end). No client-side
 * single-submit guard is warranted.
 */
export const fetchVerifyEmail = async (
  token: string,
  email: string
): Promise<FetchVerifyEmailResult> => {
  const baseURL = getBaseURL();
  let response: Response | undefined;
  try {
    response = await fetchWithCredentials(
      `${baseURL}/v3/teammates/${encodeURIComponent(token)}/verify-email`,
      {
        timeout: 10000,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }
    );
  } catch (error: unknown) {
    if (!(error instanceof DOMException && error.name === "AbortError")) {
      throw error;
    }
  }
  if (!response) {
    return defaultVerifyEmailError;
  }
  let body: FetchVerifyEmailResponse;
  try {
    body = await response.json();
  } catch {
    return defaultVerifyEmailError;
  }
  // 422 is returned when the email address is not verified and the details
  // of the failed verification are returned in the body of the response.
  if (response.status === 422) {
    // Defensive check to ensure error_id is set
    if (!body.error_id) {
      return defaultVerifyEmailError;
    }

    return {
      verified: false,
      error_id: body.error_id,
    };
  }

  // 500 is returned when an error occurred when attempting to encode the response
  // in the Chaos endpoint and a general error is returned back to the caller
  // instead of the response generated by the endpoint logic.
  if (response.status === 500) {
    return defaultVerifyEmailError;
  }

  // 200 is returned when email address has been verified and the target URL has
  // been generated successfully.
  if (response.status === 200) {
    // Honor the server's explicit verified flag rather than inferring verification from the
    // 200 status alone. Per the Chaos contract a 200 carries verified:true (non-verification
    // is signaled by a 422), but if the backend ever returns 200 with verified:false we must
    // NOT redirect into the linking flow. We reject only an explicit false so an omitted flag
    // still degrades to the documented "200 == verified" behavior.
    if (body.verified === false) {
      return defaultVerifyEmailError;
    }

    // Defensive check to ensure target is set
    if (!body.target) {
      return defaultVerifyEmailError;
    }

    return {
      verified: true,
      target: body.target,
    };
  }

  // default return for any other status code returned; only 200, 422, and 500 are
  // coded into the Chaos endpoint to return back, but this safeguards us against
  // an unexpected status code or failure.
  return defaultVerifyEmailError;
};
